import { getSshPublicKeyError } from './ssh-public-key.util.js';
// Helpers for the SFTP identity a user authenticates with.
//
// An SFTP user's login name is their Keycloak username, which defaults to their
// email address unless an explicit SFTP username is set. AWS Transfer Family
// accepts a narrower character set than email does, so an address that is
// perfectly valid for web login can be unusable over SFTP.
/**
 * Characters AWS Transfer Family accepts in a username, per its documented rules
 * for custom identity providers: 3-100 characters from a-z, A-Z, 0-9, `_`, `-`,
 * `.`, `@`, and not starting with `-`, `.`, or `@`.
 *
 * Notably this excludes `+`, so an address like `user+sftp@example.com` is a
 * valid email and a valid Keycloak username but not a usable SFTP login.
 *
 * @see https://docs.aws.amazon.com/transfer/latest/userguide/custom-identity-provider-users.html
 */
const TRANSFER_FAMILY_USERNAME_REGEX = /^[\w][\w@.-]{2,99}$/;
/** Minimum Keycloak username length (matches the realm's username validator). */
export const MIN_SFTP_USERNAME_LENGTH = 3;
export const SFTP_USERNAME_LENGTH_MESSAGE = `SFTP username must be at least ${MIN_SFTP_USERNAME_LENGTH} characters when set (Keycloak username policy)`;
export const SSH_KEYS_REQUIRE_SFTP_MESSAGE = 'SSH public keys can only be set for SFTP-enabled users';
/**
 * SFTP access is scoped to a single client's directory, and internal users are
 * cross-client by definition, so they are never eligible for it.
 *
 * Before the access-tier refactor this was only prevented incidentally — an
 * internal user's party had no derivable SFTP group — rather than by any rule.
 * Stating it explicitly keeps the invariant from silently lapsing if group
 * derivation or the client list ever changes.
 */
export const SFTP_NOT_FOR_INTERNAL_MESSAGE = 'SFTP access is not available for SteerFi internal users';
/**
 * Whether an SFTP username satisfies the Keycloak minimum. Blank is allowed and
 * means "use the email address".
 */
export const isSftpUsernameLengthValid = (value) => {
    if (value === undefined || value === null)
        return true;
    const trimmed = value.trim();
    return trimmed === '' || trimmed.length >= MIN_SFTP_USERNAME_LENGTH;
};
/**
 * Whether SSH public keys are permitted given a user's SFTP state. Keys are only
 * meaningful for SFTP users.
 */
export const sshKeysAllowedForSftpState = (couldBeSftp, keys) => {
    if (couldBeSftp)
        return true;
    return keys === undefined || keys === null || keys.length === 0;
};
/**
 * Validate a form's SFTP fields before submitting, returning the first error
 * message or null when they are acceptable.
 *
 * Applies the same rules the API enforces, so both the create and edit forms fail
 * fast with identical messages instead of round-tripping to get a 400.
 */
export const getSftpFieldsError = (fields) => {
    if (!isSftpUsernameLengthValid(fields.sftpUsername)) {
        return SFTP_USERNAME_LENGTH_MESSAGE;
    }
    for (const [index, key] of (fields.sshPublicKeys ?? []).entries()) {
        const keyError = getSshPublicKeyError(key);
        if (keyError)
            return `SSH public key ${index + 1}: ${keyError}`;
    }
    return null;
};
/**
 * Whether a login name is usable as an AWS Transfer Family SFTP username.
 */
export const isSftpSafeUsername = (username) => TRANSFER_FAMILY_USERNAME_REGEX.test((username ?? '').trim());
/**
 * The username an SFTP user will actually authenticate with: the explicit SFTP
 * username when set, otherwise the email address.
 */
export const resolveSftpUsername = (email, sftpUsername) => {
    const explicit = (sftpUsername ?? '').trim();
    return explicit !== '' ? explicit : (email ?? '').trim();
};
/**
 * Returns a warning message when the effective SFTP login name will not work
 * over SFTP, or null when it is usable.
 *
 * Advisory rather than a hard validation error: the character set is enforced by
 * AWS Transfer Family at login, not by us, so the goal is to surface the problem
 * at the point of data entry instead of silently creating an account that cannot
 * authenticate.
 *
 * @example
 * getSftpUsernameWarning('user+sftp@example.com')
 * // "…contains characters AWS Transfer Family does not accept…"
 * getSftpUsernameWarning('user@example.com') // null
 */
export const getSftpUsernameWarning = (email, sftpUsername) => {
    const effective = resolveSftpUsername(email, sftpUsername);
    if (effective === '')
        return null;
    if (effective.length < MIN_SFTP_USERNAME_LENGTH) {
        return `The SFTP login name "${effective}" is shorter than ${MIN_SFTP_USERNAME_LENGTH} characters.`;
    }
    if (!isSftpSafeUsername(effective)) {
        return `The SFTP login name "${effective}" contains characters AWS Transfer Family does not accept (only letters, numbers, and @ . _ - are allowed). Set an explicit SFTP username, or SFTP logins will fail.`;
    }
    return null;
};
/**
 * Slugify a company name for use in a Keycloak SFTP group path.
 * Canonical implementation — the API's keycloak-group.util.ts re-exports this,
 * and the web app uses it to preview the group path before saving.
 */
export const generateCompanySlug = (companyName) => companyName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
/** Root path for SFTP client groups in Keycloak. */
export const SFTP_CLIENTS_GROUP_PATH = '/sftp/clients';
/**
 * The Keycloak group path for a client's SFTP users, e.g.
 * `/sftp/clients/acme-corporation`. Returns '' for a blank company name so
 * callers can treat "no client selected" uniformly.
 */
export const generateSftpGroupPath = (companyName) => {
    const slug = generateCompanySlug((companyName ?? '').trim());
    return slug === '' ? '' : `${SFTP_CLIENTS_GROUP_PATH}/${slug}`;
};
//# sourceMappingURL=sftp-username.util.js.map